import { useCallback, useMemo } from "react";

import {
  ButtonStyle,
  CopyToClipboard,
  EllipsisIcon,
  MenuButton,
  MenuItemStyle,
  Status,
  StatusLight,
  Table,
  TableCellType,
  TableRow,
  TrashIcon,
} from "@prequel/shared-components";

import { useAllMagicLinks, useDeleteMagicLink } from "@/api/queries/magic-links";
import { ExistingMagicLink, getTimeRemaining } from "@/types";
import { sortByCreatedAt } from "@/types/common";

const MAGIC_LINK_TABLE_COLUMNS = [
  "Magic Link Name",
  "Id In Provider System",
  "Magic Link",
  "Status",
  "Time Remaining",
  "Host",
  "", // Empty column for action buttons
];

const MagicLinksTable = () => {
  const { data: magicLinks = [] } = useAllMagicLinks();
  const deleteMagicLinkMutation = useDeleteMagicLink();

  const mapMagicLinkToRow = useCallback(
    (link: ExistingMagicLink): TableRow => ({
      id: link.id,
      cells: [
        { value: link.name },
        { value: link.id_in_provider_system },
        {
          value: (
            <div className="flex max-w-xs items-center">
              <CopyToClipboard textToCopy={link.link} />
              <span className="truncate">{link.link}</span>
            </div>
          ),
        },
        {
          value: (
            <div className="flex items-center text-gray-500">
              <StatusLight status={link.has_been_used ? Status.INACTIVE : Status.ACTIVE} />
              {link.has_been_used ? "Submitted" : "Active"}
            </div>
          ),
        },
        { value: getTimeRemaining(link.created_at) },
        {
          value: (
            <div className="flex max-w-xs items-center">
              <span className="truncate">{link.host}</span>
            </div>
          ),
        },
        {
          type: TableCellType.BUTTON,
          value: (
            <div className="m-auto flex text-xs">
              <MenuButton
                type={ButtonStyle.TERTIARY}
                spin={true}
                menuItems={[
                  {
                    label: (
                      <div className="flex items-center">
                        <TrashIcon className="mr-1 size-4" aria-hidden="true" />
                        Delete Magic Link
                      </div>
                    ),
                    style: MenuItemStyle.PRIMARY,
                    onClick: () => deleteMagicLinkMutation.mutate(link.id),
                  },
                ]}
                icon={<EllipsisIcon className="my-1 h-4 w-3 rotate-90" aria-hidden="true" />}
              />
            </div>
          ),
        },
      ],
    }),
    [deleteMagicLinkMutation],
  );

  const rows: TableRow[] = useMemo(() => {
    const sortedLinks = [...magicLinks].sort(sortByCreatedAt);
    return sortedLinks.map(mapMagicLinkToRow);
  }, [magicLinks, mapMagicLinkToRow]);

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-md border border-gray-200 bg-white p-8">
        <p className="text-sm text-gray-500">No magic links found. Magic Links will show up here.</p>
      </div>
    );
  }

  return <Table columns={MAGIC_LINK_TABLE_COLUMNS} rows={rows} />;
};

export default MagicLinksTable;
